import { userRefresh } from '@/services/Api/actions/auth'
import { isAuthTokenResponseSuccess } from '@/services/Api/utils/authTokenResponseChecker'
import { getCookie } from '@/utils/cookies'
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  TOKEN_EXPIRY_KEY,
} from './constants'

// Хранение токенов в cookie, чтобы сервер (server components) мог прочитать
// accessToken и получить пользователя на стороне сервера.
//
// Запись cookie выполняется на сервере — в server actions
// (src/services/Api/actions/auth через writeAuthCookies): Set-Cookie приходит
// с ответом action. Здесь клиент только читает cookie и синхронно их очищает.

function readCookie(name: string): string | null {
  const value = getCookie(name)

  return value != null && value !== '' ? decodeURIComponent(value) : null
}

// Атрибуты удаления должны соответствовать серверной записи
// (AUTH_COOKIE_OPTIONS): Partitioned адресует ту же CHIPS-«банку» (в
// браузерах без CHIPS атрибут просто игнорируется), Secure обязателен
// для SameSite=None. На http://localhost браузеры разрешают Secure cookie
// без HTTPS.
function eraseCookie(name: string) {
  document.cookie = `${name}=; path=/; samesite=none; secure; partitioned; max-age=0`
}

export class TokenStorage {
  getAccessToken(): string | null {
    return readCookie(ACCESS_TOKEN_KEY)
  }

  // Получение токена с запуском рефреша если он истек
  async getActualAccessToken() {
    const token = this.getAccessToken()

    if (token != null) {
      await this.updateExpiredToken()
    }

    return this.getAccessToken()
  }

  getRefreshToken(): string | null {
    return readCookie(REFRESH_TOKEN_KEY)
  }

  getTokenExpiry(): number {
    const expiry = readCookie(TOKEN_EXPIRY_KEY)

    return expiry != null ? parseInt(expiry, 10) : 0
  }

  isTokenExpired(): boolean {
    return Date.now() - 10000 >= this.getTokenExpiry()
  }

  // Попытка рефрешнуть токен если он истек
  async updateExpiredToken() {
    if (this.isTokenExpired()) {
      await this.refresh()
    }
  }

  clearTokens() {
    if (typeof window == 'undefined') {
      return null
    }

    eraseCookie(ACCESS_TOKEN_KEY)
    eraseCookie(REFRESH_TOKEN_KEY)
    eraseCookie(TOKEN_EXPIRY_KEY)
  }

  hasTokens(): boolean {
    return !!this.getAccessToken() && !!this.getRefreshToken()
  }

  private _refreshTokenPromise: Promise<boolean> | null = null

  async refresh() {
    if (this._refreshTokenPromise != null) {
      return this._refreshTokenPromise
    }

    this._refreshTokenPromise = this._refresh().finally(() => {
      this._refreshTokenPromise = null
    })

    return this._refreshTokenPromise
  }

  private async _refresh() {
    try {
      const refreshToken = this.getRefreshToken()

      if (refreshToken == null) {
        return false
      }

      const response = await userRefresh(refreshToken)

      // Новые токены в cookie уже записаны сервером внутри userRefresh
      return isAuthTokenResponseSuccess(response)
    } catch {
      return false
    }
  }
}

export const tokenStorage = new TokenStorage()
